import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { auth as authApi } from "@/lib/api";
import { RivetWordmark } from "@/components/RivetLogo";
import { IDS } from "@/constants/testIds";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "signup" ? "signup" : "login";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const handleGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    setBusy(true);
    try {
      const redirectUri = window.location.origin + "/auth/google";
      const { data } = await authApi.googleAuthorize(redirectUri);
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Google sign-in isn't configured for this domain — use email & password below.");
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        await register({ email, password, name });
        toast.success("Welcome to RivetQuote!");
        nav("/onboarding");
      } else {
        await login(email, password);
        toast.success("Welcome back");
        nav("/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auth failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={IDS.auth.loginPage} className="min-h-screen bg-[#F1F5F9] flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Link to="/"><RivetWordmark size={40} /></Link>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-7">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">
            {mode === "signup" ? "Create account" : "Welcome back"}
          </div>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-[#001F3F]" style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
            {mode === "signup" ? "Start winning jobs faster." : "Sign in to RivetQuote"}
          </h1>

          <div className="mt-6">
            <button
              data-testid={IDS.auth.googleBtn}
              onClick={handleGoogle}
              className="w-full h-11 rounded-full border border-slate-300 bg-white text-[#001F3F] font-medium text-sm hover:bg-slate-50 transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.7 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.7c-.6 3-2.2 5.6-4.7 7.4l7.3 5.7c4.3-4 6.7-9.9 6.7-17.4z"/><path fill="#FBBC05" d="M10.5 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C.9 16.4 0 20.1 0 24s.9 7.6 2.6 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 12-2.1 16-5.8l-7.3-5.7c-2 1.4-4.6 2.2-8.7 2.2-6.3 0-11.6-4.2-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
              Continue with Google
            </button>
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200"/>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">or</div>
            <div className="flex-1 h-px bg-slate-200"/>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Your name</Label>
                <Input
                  data-testid={IDS.auth.nameInput}
                  id="name" type="text" required value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-200"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Email</Label>
              <Input
                data-testid={IDS.auth.emailInput}
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-200"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Password</Label>
              <Input
                data-testid={IDS.auth.passwordInput}
                id="password" type="password" required minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11 border-slate-200 focus-visible:ring-blue-200"
              />
            </div>
            <Button
              data-testid={IDS.auth.submitBtn}
              type="submit" disabled={busy}
              className="w-full h-11 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold"
            >
              {busy ? "Please wait…" : mode === "signup" ? "Create account" : "Sign in"} <ArrowRight size={16} className="ml-1"/>
            </Button>
          </form>

          <div className="mt-5 text-center text-sm text-[#64748B]">
            {mode === "signup" ? "Already have an account?" : "New to RivetQuote?"}{" "}
            <button
              data-testid={IDS.auth.switchModeBtn}
              onClick={() => setMode(mode === "signup" ? "login" : "signup")}
              className="text-[#2563EB] font-semibold hover:underline"
            >
              {mode === "signup" ? "Sign in" : "Create an account"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
